import { useState, useEffect, useCallback } from 'react'
import {
  RefreshControl,
  ScrollView,
  Text,
  TouchableOpacity,
  View
} from 'react-native'
import { Ionicons } from '@expo/vector-icons'

// styles
import { colorPalette, globalStyles } from '../../static/styles/global'

// components & screens
import Card from '../../components/shared/card/Card'
import CardStatus from '../../components/shared/card/CardStatus'
import CustomButton from '../../components/shared/button/Button'
import CardSeparator from '../../components/shared/card/CardSeparator'
import MessageBoxPopup from '../../components/store/MessageBoxPopup'
import TwoColumnDisplay from '../../components/shared/grid/TwoColumnDisplay'
import OneColumnDisplay from '../../components/shared/grid/OneColumnDisplay'
import ConfirmStoreInformationPopup from '../../components/store/ConfirmStoreInformationPopup'
import { Loading } from '../../components/shared/navigator/Loading'
import MyScrollView from '../../components/shared/navigator/MyScrollView'

import { useInitializeStore } from '../../stores/initialize'
import { useStoreStore } from '../../stores/storeInfo'

import { phoneFormat, dateFormat } from '../../helpers/storeHelper'
import ScreenStatus from '../../components/home/ScreenStatus'
import {
  StoreStatusesLocked,
  StoreStatusesLockedKeys
} from '../../types/storeTypes'
import StoreSkeleton from '../../skeletons/StoreSkeleton'
import HomeDetailsHeader from '../../components/home/HomeDetailsHeader'
import { useTranslation } from 'react-i18next'

const StoreInformationScreen = ({ navigation, route }): JSX.Element => {
  const [correctModalVisible, setCorrectModalVisible] = useState(false)
  const [incorrectModalVisible, setIncorrectModalVisible] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // params
  const { params } = route
  const { mostRecentUserOnBoardings: userId } = params

  // stores' data
  const {
    storeData: storeData,
    error: error,
    loading: loading,
    fetchData,
    updateData
  } = useStoreStore()

  const { t } = useTranslation()

  const handleMessageSent = (message: string) => {
    const data = {
      submitted: false,
      message: message
    }
    updateData(storeData.essUserOnBoardingKey, data)
  }

  const handleConfirmation = () => {
    updateData(storeData.essUserOnBoardingKey, { submitted: true, message: '' })
  }

  // pull down to refresh the screen
  const onRefresh = useCallback(() => {
    setRefreshing(true)
    setTimeout(() => {
      navigation.replace('StoreInformation', { refresh: Math.random() })
      setRefreshing(false)
    }, 1000)
  }, [])

  useEffect(() => {
    fetchData(userId)
  }, [])

  if (loading) return <StoreSkeleton />

  if (error) return <ScreenStatus message={error} />

  return (
    storeData && (
      <MyScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        keyboardShouldPersistTaps={'always'}
      >
        <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
          <View style={[globalStyles.horizontalPadGap]}>
            <HomeDetailsHeader
              title={t('storeinformation')}
              description={storeData?.storeStatusValue != 'D' ? t('storedescription') : t('storedescription2')}
            />
          </View>
        </View>
        <View style={[globalStyles.containerOverlap]}>
          <Card>
            <Text
              style={[
                globalStyles.text,
                globalStyles.fwBold,
                globalStyles.fs18,
                globalStyles.blackTextColor
              ]}
            >
              {t('hiringDetails')}
            </Text>
            <CardSeparator light={true} />
            <TwoColumnDisplay
              firstTitle={t('hiredate')}
              firstDescription={dateFormat(storeData?.hireDate)}
              secondTitle={t('startdate')}
              secondDescription={dateFormat(storeData.pendingStartDate)}
            />
            <CardSeparator light={true} />
            <OneColumnDisplay
              title={
                t('storename')
                //+
                // (storeData?.storeNumber
                //   ? ' ' + storeData?.storeNumber.toString()
                //   : '')
              }
              description={
                //storeData?.brandingName
                // +
                // '\n' +
                (storeData?.storeName ? storeData?.storeName : '')
              }
            />
            <OneColumnDisplay
              title={''}
              description={[
                storeData?.storeAddress1,
                storeData?.storeAddress2,
                storeData?.storeAddress3,
                phoneFormat(storeData?.storePhoneNumber)
              ]
                .filter((s) => !!s)
                .join('\n')}
              gap={20}
            />
            <OneColumnDisplay
              title={t('department')}
              description={storeData?.department}
              gap={20}
            />
            <OneColumnDisplay
              title={t('employmentrole')}
              description={storeData?.employeeRole}
            />
            <CardSeparator light={true} />
            <TwoColumnDisplay
              firstTitle={t('paytype')}
              firstDescription={storeData?.perPayTypeDescription}
              secondTitle={t('payfrequency')}
              secondDescription={storeData?.perPayCycleDescription}
              gap={20}
            />
            <TwoColumnDisplay
              firstTitle={t('payrate')}
              firstDescription={`$${storeData?.rate?.toFixed(2)} ${storeData?.perPayTypeDescription === "Salary" ? t('*Annual rate') : ""}`}
              secondTitle={t('employmentstatus')}
              secondDescription={storeData?.perScheduleDescription}
              gap={20}
            />
            {storeData?.scheduledHours != null ?
              <OneColumnDisplay
                title={t('Scheduledhours')}
                description={`${storeData?.scheduledHours?.toFixed(2)}`}
                gap={20}
              /> : <></>
            }
          </Card>
          {!StoreStatusesLockedKeys.includes(storeData.storeStatusValue) ? (
            <View style={[globalStyles.mt2, globalStyles.mb4]}>
              <CustomButton
                text={t('my information is correct')}
                onPress={
                  //setCorrectModalVisible(!correctModalVisible)
                  handleConfirmation
                }
              />
              {storeData?.storeStatusValue != 'D' &&
                <TouchableOpacity
                  onPress={() => {
                    setIncorrectModalVisible(!incorrectModalVisible)
                  }}
                >
                  <Text
                    style={[
                      globalStyles.text,
                      globalStyles.textCenter,
                      globalStyles.textUnderline,
                      globalStyles.secondaryTextColor,
                      globalStyles.mt3
                    ]}
                  >
                    {t('there are incorrect details')}
                  </Text>
                </TouchableOpacity>
              }
            </View>
          ) : (
            <>
              <View
                style={[globalStyles.mb5, globalStyles.ml3, globalStyles.mr3]}
              >
                <CustomButton
                  text={t('home')}
                  onPress={() => {
                    navigation.replace('Onboarding')
                  }}
                />
              </View>
            </>
          )}
          {/* status = not started */}
        </View>

        {/* <ConfirmStoreInformationPopup
          action={handleConfirmation}
          visible={correctModalVisible}
          setVisible={setCorrectModalVisible}
          setMsgBoxVisibility={setIncorrectModalVisible}
        /> */}
        <MessageBoxPopup
          title={t('incorrect store information')}
          visible={incorrectModalVisible}
          setVisible={setIncorrectModalVisible}
          action={handleMessageSent}
          userId={storeData?.essUserOnBoardingKey}
        />
        {/* {!StoreStatusesLockedKeys.includes(storeData.storeStatusValue) ? (
          <View style={[globalStyles.mb5, globalStyles.ml3, globalStyles.mr3]}>
            <CustomButton
              text={t('cancel')}
              onPress={() => {
                navigation.replace('Onboarding')
              }}
              color="warn"
            />
          </View>
        ) : (
          <></>
        )} */}
      </MyScrollView>
    )
  )
}

export default StoreInformationScreen
